import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { capitalize, styled } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
/**
 * NOTE:
 * These components are adapted directly from Material-UI's Button implementation.
 * - They allow us to use `MUIButtonStartIcon` and `MUIButtonEndIcon` independently, especially when using `ButtonBase` or custom buttons.
 * - These components ensure consistency with MUI's default button styling.
 */
// Define common styles for icons (start and end) based on button sizes.
const commonIconStyles = [
    {
        props: { size: "small" },
        style: {
            "& > *:nth-of-type(1)": {
                fontSize: 18, // Icon font size for small buttons.
            },
        },
    },
    {
        props: { size: "medium" },
        style: {
            "& > *:nth-of-type(1)": {
                fontSize: 20, // Icon font size for medium buttons.
            },
        },
    },
    {
        props: { size: "large" },
        style: {
            "& > *:nth-of-type(1)": {
                fontSize: 22, // Icon font size for large buttons.
            },
        },
    },
];
// Styled component for the start icon slot of a button.
export const MUIButtonIcon = styled("span", {
    name: "MuiButton",
    slot: "Icon",
    overridesResolver: (props, styles) => {
        const { ownerState } = props;
        return [
            (ownerState === null || ownerState === void 0 ? void 0 : ownerState.position) && styles[`${ownerState.position}Icon`],
            (ownerState === null || ownerState === void 0 ? void 0 : ownerState.size) && styles[`iconSize${capitalize(ownerState.size)}`], // Apply size-specific styles.
        ].filter(Boolean);
    },
    shouldForwardProp: (prop) => !["shouldRotateIcon", "marginLeft", "position"].includes(prop), // Cast `prop` to string
})(({ shouldRotateIcon, size = "medium", position, marginRight, marginLeft }) => {
    // Define default margins based on position
    const defaultMargins = position === "start"
        ? { marginRight: marginRight !== null && marginRight !== void 0 ? marginRight : 8, marginLeft: marginLeft !== null && marginLeft !== void 0 ? marginLeft : -4 }
        : { marginRight: marginRight !== null && marginRight !== void 0 ? marginRight : -4, marginLeft: marginLeft !== null && marginLeft !== void 0 ? marginLeft : 8 };
    return Object.assign(Object.assign(Object.assign({ display: "inherit" }, defaultMargins), (size === "small" && {
        [position === "start" ? "marginLeft" : "marginRight"]: -2, // Adjust margin for small buttons.
    })), { transform: shouldRotateIcon ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms" });
});
/**
 * Sets default props for optional fields.
 * For required fields like position, no default is set.
 */
MUIButtonIcon.defaultProps = {
    shouldRotateIcon: false,
    size: "medium",
};
/**
 * Example Usage:
 * Below is a very basic implementation of these components.
 */
export const BasicExample = () => {
    const [isOpen, setIsOpen] = useState(false);
    // Toggle the state when button is clicked
    const handleToggle = () => setIsOpen(!isOpen);
    return (_jsxs(ButtonBase, Object.assign({ onClick: handleToggle }, { children: [_jsx(MUIButtonIcon, Object.assign({ shouldRotateIcon: isOpen, position: "start", size: "medium" }, { children: _jsx(Icon, { children: "expand_more" }) })), "Button Text", _jsx(MUIButtonIcon, Object.assign({ shouldRotateIcon: isOpen, position: "end", size: "medium" }, { children: _jsx(Icon, { children: "expand_more" }) }))] })));
};
